import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useUserRole } from "@/hooks/useUserRole";
import { useSupabase } from "@/hooks/useSupabase";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PaymentMethodForm, PaymentMethodFormData } from "./subscription/PaymentMethodForm";
import { SubscriptionDetails } from "./subscription/SubscriptionDetails";
import { Loader2, ExternalLink } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSubscription } from "@/hooks/useSubscription";

export const SubscriptionSection = () => {
  const { userRole, isLoading: isRoleLoading } = useUserRole();
  const { isLoggedIn } = useSupabase();
  const { toast } = useToast();
  const [isUpdatePaymentOpen, setIsUpdatePaymentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [portalSetupRequired, setPortalSetupRequired] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  
  const { 
    subscriptionTier, 
    nextBilling, 
    isCheckingAccess, 
    paymentMethod
  } = useSubscription();

  const handleCustomerPortal = async () => {
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please login to manage your subscription.",
        variant: "destructive",
      });
      return;
    }

    setIsPortalLoading(true);
    setPortalError(null);
    setPortalSetupRequired(false);

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) {
        console.error('Customer portal error:', error);
        setPortalError("We couldn't open the subscription management portal. Please try again later.");
        throw error;
      }
      
      if (data?.error) {
        console.error('Portal session error:', data.error);
        setPortalError(data.error || "We couldn't open the subscription management portal. Please try again later.");
        return;
      }
      
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        setPortalError("No portal URL returned. Please try again later.");
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error:', error);
      if (!portalError) {
        setPortalError("We couldn't open the subscription management portal. Please try again later.");
      }
      toast({
        title: "Subscription Management Error",
        description: "We couldn't open the subscription management portal. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleUpdatePayment = async (values: PaymentMethodFormData) => {
    setIsSubmitting(true);
    setPaymentError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('update-payment-method', {
        body: { paymentDetails: values }
      });
      
      if (error) throw error;

      toast({
        title: "Payment method updated",
        description: "Your payment method has been successfully updated.",
      });
      setIsUpdatePaymentOpen(false);
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error:', error);
      
      let errorMessage = "Failed to update payment method. Please try again.";
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      setPaymentError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isRoleLoading || isCheckingAccess;

  return (
    <div className="grid gap-8">
      <Card>
        <CardHeader>
          <CardTitle>Current Subscription</CardTitle>
          <CardDescription>Manage your subscription plan</CardDescription>
        </CardHeader>
        <CardContent>
          {portalError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {portalError}
                {portalSetupRequired && (
                  <div className="mt-2">
                    <p className="text-sm">
                      The Stripe Customer Portal needs to be configured in your Stripe Dashboard.
                    </p>
                    <a 
                      href="https://dashboard.stripe.com/test/settings/billing/portal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 flex items-center text-sm font-medium text-blue-600 hover:text-blue-500"
                    >
                      Configure Stripe Portal
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
          <SubscriptionDetails
            userRole={userRole || subscriptionTier || 'basic'}
            nextBilling={nextBilling}
            isLoading={isLoading}
            paymentMethod={paymentMethod}
          />
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button
            className="w-full"
            variant="outline"
            onClick={() => setIsUpdatePaymentOpen(true)}
          >
            Update Payment Method
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleCustomerPortal}
            disabled={isPortalLoading}
          >
            {isPortalLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Opening...
              </>
            ) : (
              'Manage Subscription'
            )}
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={isUpdatePaymentOpen} onOpenChange={setIsUpdatePaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Payment Method</DialogTitle>
            <DialogDescription>
              Enter your new card details below. Your card information is securely processed by Stripe.
            </DialogDescription>
          </DialogHeader>
          
          {paymentError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{paymentError}</AlertDescription>
            </Alert>
          )}
          
          <PaymentMethodForm
            onSubmit={handleUpdatePayment}
            isSubmitting={isSubmitting}
            onCancel={() => setIsUpdatePaymentOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
